/* @flow */
import * as React from 'react';

import EventTracking from 'lib/EventTracking';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Text from 'components/Text';
import MediaObject from 'components/MediaObject';
import List from 'components/List';
import ListItem from 'components/ListItem';
import Link from 'components/Link';
import Heading from 'components/Heading';
import Button from 'components/Button';
import Divider from 'components/Divider';
import Row from 'components/Row';

import type { IndividualPlanView as IndividualPlanViewQuery } from 'settings/queries/__generated__/IndividualPlanView';

type NoMaybeMeType = $NonMaybeType<$ElementType<IndividualPlanViewQuery, 'me'>>;
type IndividualPlansType = $ElementType<$ElementType<NoMaybeMeType, 'individualPlans'>, number>;
type PlanUnionType = $ElementType<IndividualPlansType, 0>;

type PurchasedIndividualPlanType = $ElementType<PlanUnionType, 2>;
type PurchasedPlanProductDetailsType = $ElementType<
  $ElementType<PurchasedIndividualPlanType, 'productDetails'>,
  number
>;

type Props = {
  inPaidOrg: boolean,
  plan: PurchasedIndividualPlanType,
};

class PurchasedPlanView extends React.Component<Props> {
  renderPlan = (inPaidOrg: boolean, plan: PurchasedIndividualPlanType) => {
    const IndividualPlanSection = (
      <Box
        title={
          <MediaObject center figure="right">
            <MediaObject center figure="left">
              <Text align="left" size="medium" weight="semibold">
                Individual Plan
              </Text>
              <Text align="left" color="eclipse" data-test="active-header" size="small">
                {'Active since ' + plan.purchasedAt}
              </Text>
            </MediaObject>
            <Text size="small">You are currently on this plan</Text>
          </MediaObject>
        }
        dark
        headerSize="xsmall">
        <BoxBody size="small">
          {this.renderPlanFeatures(plan.productDetails)}
          {this.renderBilling(inPaidOrg, plan.billingInfoText, undefined)}
        </BoxBody>
      </Box>
    );

    return IndividualPlanSection;
  };

  renderBilling = (inPaidOrg: boolean, billingInfoText?: string, cancelLink?: string) => {
    const buttons = (
      <React.Fragment>
        {inPaidOrg && (
          <Row>
            <Text align="center" color="lava" data-test="in-paid-cancel" size="small">
              You are already covered by a School &amp; District plan. You may cancel your
              subscription
            </Text>
          </Row>
        )}
        <MediaObject figure="left">
          <Button
            onClick={() => {
              if (inPaidOrg) {
                EventTracking.trackKey('individual_plans.plan_view.paid_org_cancel.click');
              } else {
                EventTracking.trackKey('individual_plans.plan_view.cancel.click');
              }
            }}
            href={cancelLink}
            color="lava"
            level="primary">
            Cancel subscription
          </Button>
          <React.Fragment />
        </MediaObject>
      </React.Fragment>
    );

    const billingSection = (
      <Row size="small">
        <Heading align="left" level="3">
          Billing
        </Heading>
        <Text align="left" size="small">
          {billingInfoText}
        </Text>
      </Row>
    );

    return (
      <React.Fragment>
        {billingSection}
        <Divider size="small" />
        {buttons}
      </React.Fragment>
    );
  };

  renderPlanFeatures = (purchasedPlanFeatures: Array<PurchasedPlanProductDetailsType>) => {
    const listItems = purchasedPlanFeatures.map(item => (
      <ListItem>
        <Text size="small">{item.text}</Text>
        {item.learnMoreLink ? (
          <Link href={item.learnMoreLink} weight="semibold">
            {' Learn More '}
          </Link>
        ) : null}
      </ListItem>
    ));

    return (
      <Row size="small">
        <Heading align="left" level="3">
          Features
        </Heading>
        <List marker="bullet" markerPosition="inside" size="medium">
          {listItems}
        </List>
      </Row>
    );
  };

  render() {
    const { inPaidOrg, plan } = this.props;
    return <React.Fragment>{this.renderPlan(inPaidOrg, plan)}</React.Fragment>;
  }
}

export default PurchasedPlanView;
